import type { RungLink } from '@/features/funnel';
import type { NavItem } from './navItems';

/**
 * THE SUMMON — the navigation overlay (port of assets/omega-summon.js, docs/behavior/ui-layer.md
 * §1.2 and §5.3, repaired per UI-R3). Pure logic only; the component lives in SummonOverlay.tsx.
 */

/** Noise glyphs the labels decode from (17 code points). */
export const SUMMON_NOISE: readonly string[] = Array.from('ΩΦ☧☉☿⚗⚚☾⚹𓂀ΞΔΣΨΛ01');
/** Decode: 10 ticks (frame 0 … 9) every 26 ms, row i starting 40 + 45·i ms after opening. */
export const DECODE_LAST_FRAME = 9;
export const DECODE_TICK_MS = 26;
export const FOCUS_DELAY_MS = 30;

export function decodeDelay(index: number): number {
  return 40 + index * 45;
}

export interface SummonRow {
  readonly href: string;
  readonly label: string;
  readonly glyph: string;
  readonly glyphHtml?: string;
  /** Earned funnel rung (listed above the separator). */
  readonly gated: boolean;
  /** Revealed rung with no product behind it. */
  readonly unforged: boolean;
}

export type SummonEntry =
  { readonly kind: 'row'; readonly row: SummonRow; readonly index: number } | { readonly kind: 'separator' };

const ALPHANUMERIC = /[A-Za-z0-9]/;

/** A rung label such as `♄ REX UMBRARUM` is split into its leading glyph and the words. */
export function splitLeadingGlyph(text: string): { readonly glyph: string; readonly label: string } {
  const label = text.replace(/\s+/g, ' ').trim();
  const lead = Array.from(label)[0];
  if (lead === undefined || ALPHANUMERIC.test(lead)) return { glyph: '', label };
  return { glyph: lead, label: label.slice(lead.length).trim() };
}

/**
 * The rows, in production order: earned rungs first (ladder order), then the permanent items.
 * Rows are de-duplicated by href (first wins, `gated` is kept if either copy has it). `hrefFor`
 * applies the front-door rule to permanent items.
 */
export function summonRows(
  rungs: readonly RungLink[],
  items: readonly NavItem[],
  hrefFor: (href: string) => string,
): SummonRow[] {
  const candidates: SummonRow[] = [
    ...rungs.map((rung) => {
      const { glyph, label } = splitLeadingGlyph(rung.label);
      return { href: rung.href, label, glyph, gated: true, unforged: !rung.forged };
    }),
    ...items.map((item) => ({
      href: hrefFor(item.href),
      label: item.label,
      glyph: item.glyph,
      ...(item.glyphHtml === undefined ? {} : { glyphHtml: item.glyphHtml }),
      gated: false,
      unforged: false,
    })),
  ];
  const unique: SummonRow[] = [];
  for (const row of candidates) {
    if (row.label === '') continue;
    const seen = unique.findIndex((existing) => existing.href === row.href);
    if (seen === -1) unique.push(row);
    else if (row.gated) unique[seen] = { ...(unique[seen] as SummonRow), gated: true };
  }
  return [...unique.filter((row) => row.gated), ...unique.filter((row) => !row.gated)];
}

/** Rows with one separator where the gated half ends — only when there is a gated half. */
export function summonEntries(rows: readonly SummonRow[]): SummonEntry[] {
  const entries: SummonEntry[] = [];
  let sawGated = false;
  let drewSeparator = false;
  rows.forEach((row, index) => {
    if (!drewSeparator && sawGated && !row.gated) {
      drewSeparator = true;
      entries.push({ kind: 'separator' });
    }
    entries.push({ kind: 'row', row, index });
    if (row.gated) sawGated = true;
  });
  return entries;
}

export function wrapIndex(index: number, count: number): number {
  if (count <= 0) return 0;
  return ((index % count) + count) % count;
}

/** One decode tick: characters before (frame / 9) · length are real, the rest noise; spaces stay. */
export function decodeFrame(text: string, frame: number, random: () => number = Math.random): string {
  let out = '';
  for (let i = 0; i < text.length; i += 1) {
    const ch = text.charAt(i);
    if (ch === ' ') out += ' ';
    else if (i < (frame / DECODE_LAST_FRAME) * text.length) out += ch;
    else out += SUMMON_NOISE[Math.floor(random() * SUMMON_NOISE.length)] ?? '';
  }
  return out;
}

export type SummonKeyAction = 'toggle' | 'close' | 'next' | 'previous' | 'activate' | 'trap';

export interface KeyLike {
  readonly key: string;
  readonly ctrlKey: boolean;
  readonly metaKey: boolean;
}

/**
 * Keyboard contract. Ctrl/⌘+K toggles from page load (UI-R3; production bound it only after the
 * first open). While open: Esc closes, ↑/↓ move with wrap-around, Enter follows the selected row,
 * Tab keeps focus inside the dialog.
 */
export function summonKeyAction(event: KeyLike, open: boolean): SummonKeyAction | null {
  if ((event.metaKey || event.ctrlKey) && (event.key === 'k' || event.key === 'K')) return 'toggle';
  if (!open) return null;
  switch (event.key) {
    case 'Escape':
      return 'close';
    case 'ArrowDown':
      return 'next';
    case 'ArrowUp':
      return 'previous';
    case 'Enter':
      return 'activate';
    case 'Tab':
      return 'trap';
    default:
      return null;
  }
}

/** The row for the page being viewed (`aria-current="page"`). Trailing slashes are ignored. */
export function isCurrentRow(href: string, pathname: string): boolean {
  const normalise = (path: string) => (path.length > 1 ? path.replace(/\/+$/, '') : path);
  return normalise(href) === normalise(pathname);
}
